import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Collapse,
  Spin,
  Typography
} from 'antd';
import {
  MinusOutlined,
  PlusOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import React, {
  useCallback,
  useEffect,
  useReducer,
  useState
} from 'react';
import '../../../components/Common/Form.css';
import {
  getCommunityEvents,
  getCommunityResources,
  getCommunityVideos
} from '../../../services/communityService';
import './ResourceSection.css';

const { Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const resourceTypes = [
  { value: 'video', label: 'Video' },
  { value: 'event', label: 'Event/Session' },
  { value: 'resource', label: 'Resource (PDF, Link etc)' }
];

const ResourcesSection = ({
  initialValues,
  onSubmit,
  status,
  setStatus,
  form
}) => {
  const [fetchingResources, toggleFetchingResources] = useReducer(
    (state) => {
      return !state;
    },
    false
  );
  const [fetchedResources, setFetchedResources] = useState(null);
  const [fetchedEvents, setFetchedEvents] = useState(null);
  const [fetchedVideos, setFetchedVideos] = useState(null);

  function getCorrectResourceList(type) {
    switch (type) {
      case 'resource':
        if (!fetchingResources && !fetchedResources)
          fetchResourcesHandler();
        return fetchedResources;
      case 'event':
        if (!fetchingResources && !fetchedEvents) fetchEventsHandler();
        return fetchedEvents;
      case 'video':
        if (!fetchingResources && !fetchedVideos) fetchVideosHandler();
        return fetchedVideos;
      default:
        return [];
    }
  }

  const setObjectId = (value, name) => {
    const fields = form.getFieldsValue();
    const { resources } = fields;
    Object.assign(resources[name], { objectId: value });
    form.setFieldsValue({ resources });
  };

  const CustomSelect = ({ type, name, ...extra }) => {
    const listData = getCorrectResourceList(type);
    return (
      <Select
        placeholder="Search and choose Resource"
        showSearch
        className="select-padding"
        loading={fetchingResources}
        notFoundContent={fetchingResources ? <Spin size="small" /> : null}
        onSelect={(value) => setObjectId(value, name)}
        filterOption={(input, option) => {
          return option?.label.toLowerCase().includes(input.toLowerCase());
        }}
        dropdownRender={(menu) => <div>{menu}</div>}>
        {listData?.map((resource, index) => {
          return (
            <Select.Option
              key={index}
              label={resource.label}
              value={resource.value}>
              <div style={{ fontWeight: 600 }}>
                {resource.label}
                <span className="type-tag">{resource.tag}</span>
              </div>
              <div>{resource.subLabel}</div>
            </Select.Option>
          );
        })}
      </Select>
    );
  };

  const fetchResourcesHandler = useCallback((resourcesSearch) => {
    toggleFetchingResources();
    getCommunityResources()
      .then((response) => {
        const data = response.data.paginatedList.map((resource) => ({
          label: resource.title,
          subLabel: resource.link,
          tag: resource.type,
          shortUrl: resource.shortUrl,
          description: resource.description,
          value: resource._id
        }));
        setFetchedResources(data);
        toggleFetchingResources();
      })
      .catch((err) => {
        setStatus({
          error: true,
          message: `${err}`
        });
      });
  });

  const fetchEventsHandler = useCallback(
    (resourcesSearch) => {
      toggleFetchingResources();
      getCommunityEvents()
        .then((response) => {
          const data = response.data.paginatedList.map((event) => ({
            label: event.title,
            subLabel: `${event.startTime} - ${event.endTime}`,
            tag: event.type,
            value: event._id
          }));

          setFetchedEvents([]);
          toggleFetchingResources();
        })
        .catch((err) => {
          setStatus({
            error: true,
            message: `${err}`
          });
        });
    },
    [fetchedEvents]
  );

  const fetchVideosHandler = useCallback(
    (resourcesSearch) => {
      toggleFetchingResources();
      getCommunityVideos()
        .then((response) => {
          const data = response.data.paginatedList.map((video) => ({
            label: video.title,
            subLabel: video?.topic,
            tag: `topicIdx: ${video?.topicIndex}, subIdx: ${video?.subIndex}`,
            value: video._id
          }));

          setFetchedVideos(data);
          toggleFetchingResources();
        })
        .catch((err) => {
          setStatus({
            error: true,
            message: `${err}`
          });
        });
    },
    [fetchedVideos]
  );

  return (
    <section>
      <Form.List name={['resources']}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, fieldKey, ...restField }) => (
              <React.Fragment key={key}>
                <Divider />
                <Space
                  style={{
                    display: 'flex',
                    marginTop: 16
                  }}
                  direction="vertical">
                  {/* Type */}
                  <Form.Item name={[name, 'type']} {...restField}>
                    <Select
                      label="Type of resource"
                      placeholder="Choose type"
                      className="select-padding"
                      dropdownRender={(menu) => <div>{menu}</div>}>
                      {resourceTypes?.map((item, index) => (
                        <Select.Option value={item.value} key={index}>
                          <Panel header={item.label}></Panel>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {/* Need to wait for first select to be selected and then get select populated with needed data */}
                  <Form.Item dependencies={[['resources', name, 'type']]}>
                    {({ getFieldValue }) => {
                      const type = getFieldValue('resources')[name]?.[
                        'type'
                      ];
                      if (!type) return null;
                      return (
                        <Form.Item
                          {...restField}
                          name={[name, 'objectId']}>
                          <CustomSelect type={type} name={name} />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                  <Form.Item
                    label="Title"
                    name={[name, 'title']}
                    rules={[
                      { required: true, message: 'Please input title!' }
                    ]}>
                    <Input />
                  </Form.Item>
                  <Form.Item
                    label="Description"
                    name={[name, 'description']}
                    rules={[
                      {
                        required: true,
                        message: 'Please input description!'
                      }
                    ]}>
                    <Input />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              </React.Fragment>
            ))}
            <Form.Item>
              <Button
                type="dashed"
                onClick={() => {
                  add();
                }}
                block
                icon={<PlusOutlined />}>
                Add resource
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </section>
  );
};

export default ResourcesSection;
